import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Pencil, Trash2, Search, Send, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProfiles, removeProfileCache, upsertProfileCache, PROFILE_KIND_LABEL, DOWNTOWN_STATUS_LABEL } from "@/lib/profiles";
import { ProfileDialog } from "@/components/ProfileDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { t } from "@/i18n";

const FILTERS = [["all", "Todos"], ["artist", "Artistas"], ["contributor", "Compositores"]];

const IdChips = ({ p }) => {
  const chips = [
    p.apple_id ? `Apple ${p.apple_id}` : p.apple_new ? "Apple: nuevo" : null,
    p.spotify_uri ? "Spotify ✓" : p.spotify_new ? "Spotify: nuevo" : null,
    p.soundcloud_url ? "SoundCloud" : null, p.amazon_asin ? `ASIN ${p.amazon_asin}` : null, p.isni ? `ISNI ${p.isni}` : null,
  ].filter(Boolean);
  return <div className="flex flex-wrap gap-1">{chips.map((c) => <span key={c} className="text-[10px] font-mono rounded border border-border px-1.5 py-0.5 text-muted-foreground">{c}</span>)}</div>;
};

export default function ProfilesPage() {
  const { user, can } = useAuth();
  const { profiles, refresh } = useProfiles();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [dialog, setDialog] = useState(null);
  const staff = can("admin", "manager");

  const rows = useMemo(() => profiles.filter((p) => (filter === "all" || p.kind === filter || p.kind === "both") && p.name.toLowerCase().includes(q.trim().toLowerCase())), [profiles, q, filter]);

  const remove = async (p) => {
    if (!window.confirm(t("¿Eliminar el perfil «{name}»? (No se borra de Downtown)", { name: p.name }))) return;
    try { await api.delete(`/profiles/${p.id}`); removeProfileCache(p.id); toast.success(t("Perfil eliminado")); } catch (e) { toast.error(errMsg(e)); }
  };
  const createInDowntown = async (p) => {
    try {
      const { data } = await api.post(`/profiles/${p.id}/downtown`);
      upsertProfileCache({ ...p, downtown_status: "in_progress", last_job_id: data.id });
      toast.success(t("Bot iniciado para «{name}»", { name: p.name }), { description: t("Bot Downtown"), action: { label: t("Abrir"), onClick: () => (window.location.href = `/bot/${data.id}`) } });
      setTimeout(refresh, 15000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{t("Directorio")}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("Artistas y compositores")}</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{t("Registra aquí a cada artista, compositor o productor con los datos que pide la distribuidora. Al escribir su nombre en un lanzamiento, se usará este perfil")}{staff ? t(" y el bot lo dará de alta en Downtown si aún no existe") : ""}.</p>
        </div>
        <Button onClick={() => setDialog({})} className="gap-2" data-testid="btn-new-profile"><UserPlus className="h-4 w-4" /> {t("Nuevo perfil")}</Button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Buscar por nombre…")} className="pl-9" data-testid="profiles-search-input" />
        </div>
        <div className="flex gap-1">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`profiles-filter-${k}`}
              className={cn("rounded-full border px-3 py-1 text-xs transition-colors", filter === k ? "border-primary/50 bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground")}>{t(l)}</button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground ml-auto" data-testid="profiles-count">{t(rows.length === 1 ? "{n} perfil" : "{n} perfiles", { n: rows.length })}</span>
      </div>
      <div className="panel overflow-hidden rise">
        <Table data-testid="profiles-table">
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>{t("Nombre")}</TableHead><TableHead>{t("Tipo")}</TableHead><TableHead>{t("Identificadores")}</TableHead>{staff && <TableHead>Downtown</TableHead>}<TableHead className="text-right">{t("Acciones")}</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-10" data-testid="profiles-empty">{t("Sin perfiles todavía. Crea el primero con «Nuevo perfil».")}</TableCell></TableRow>}
            {rows.map((p) => {
              const [label, cls] = DOWNTOWN_STATUS_LABEL[p.downtown_status] || DOWNTOWN_STATUS_LABEL.pending;
              const editable = staff || p.created_by === user.id;
              return (
                <TableRow key={p.id} data-testid={`profile-row-${p.id}`}>
                  <TableCell><p className="font-medium" data-testid={`profile-name-${p.id}`}>{p.name}</p>{p.notes && <p className="text-xs text-muted-foreground truncate max-w-[280px]">{p.notes}</p>}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t(PROFILE_KIND_LABEL[p.kind])}</TableCell>
                  <TableCell><IdChips p={p} /></TableCell>
                  {staff && <TableCell>
                    <span className={cn("text-xs rounded-full border px-2.5 py-0.5 whitespace-nowrap", cls)} data-testid={`profile-downtown-status-${p.id}`}>{t(label)}</span>
                    {p.downtown_error && <p className="text-[11px] text-destructive mt-1 max-w-[220px]">{p.downtown_error}</p>}
                    {p.last_job_id && <Link to={`/bot/${p.last_job_id}`} className="text-[11px] text-primary hover:underline block mt-1">{t("Ver job del bot")}</Link>}
                  </TableCell>}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {staff && p.downtown_status !== "in_progress" && (
                        <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={() => createInDowntown(p)} data-testid={`profile-create-downtown-${p.id}`}><Send className="h-3.5 w-3.5" /> {t(p.downtown_status === "created" || p.downtown_status === "exists" ? "Actualizar en Downtown" : "Crear en Downtown")}</Button>
                      )}
                      {staff && p.downtown_artist_id && <a href={`https://downtownartistandlabelservices.com/artists/edit/${p.downtown_artist_id}`} target="_blank" rel="noreferrer" className="h-8 w-8 grid place-items-center rounded-md hover:bg-muted text-muted-foreground" title="Abrir en Downtown"><ExternalLink className="h-4 w-4" /></a>}
                      {editable && <Button variant="ghost" size="icon" onClick={() => setDialog(p)} data-testid={`profile-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>}
                      {editable && <Button variant="ghost" size="icon" onClick={() => remove(p)} className="text-muted-foreground hover:text-destructive" data-testid={`profile-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {dialog && <ProfileDialog open onClose={() => setDialog(null)} initial={dialog} />}
    </div>
  );
}
